import { useRef, useState } from "react";
import type { TextInput } from "react-native";

import type { DosespotMedication } from "@/api/generated/models";
import { Paragraph, XStack, YStack } from "@/components";
import {
  CheckboxBadge,
  OptionBody,
  OptionBodyHoverWrapper,
  OptionSeparator,
  OtherTextInput,
} from "@/conversation/AskUserQuestionsPanel/OptionParts";
import SkipOrSubmit from "@/conversation/AskUserQuestionsPanel/SkipOrSubmit";

export type MedicationConfirmation = {
  medicationHistoryIds: number[];
  /**
   * What the patient chose, for the answer the conversation sees. Includes any free text typed into
   * the "Enter answer here" row, which rides along in the message rather than being saved as a medication.
   */
  labels: string[];
};

const nameOf = (medication: DosespotMedication): string =>
  medication.prescription_details.display_name ??
  medication.prescription_details.generic_drug_name ??
  "Unknown medication";

/** "Enter answer here" free text: one line, since this confirms a list rather than composing one. */
function SomethingElseRow({
  value,
  onChangeText,
}: {
  value: string;
  onChangeText: (value: string) => void;
}) {
  const inputRef = useRef<TextInput>(null);
  const active = value.trim().length > 0;
  return (
    <OptionBodyHoverWrapper>
      <XStack
        flex={1}
        alignItems="center"
        paddingHorizontal="$150"
        paddingVertical="$200"
        gap="$100"
        cursor="text"
        onPress={() => (active ? onChangeText("") : inputRef.current?.focus())}
      >
        <CheckboxBadge checked={active} />
        <OtherTextInput
          value={value}
          onChangeText={onChangeText}
          inputRef={inputRef}
        />
      </XStack>
    </OptionBodyHoverWrapper>
  );
}

/** Confirming which of the pulled medications the patient actually takes. */
export default function MedicationPicker({
  medications,
  emptyDetail,
  saving,
  onSkip,
  onConfirm,
}: {
  medications: DosespotMedication[];
  emptyDetail: string;
  saving: boolean;
  onSkip: () => void;
  onConfirm: (confirmation: MedicationConfirmation) => void;
}) {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [somethingElse, setSomethingElse] = useState("");
  // Exclusive with every other row, so confirming none is a deliberate answer.
  const [noneOfTheAbove, setNoneOfTheAbove] = useState(false);

  const toggle = (id: number) => {
    setNoneOfTheAbove(false);
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const changeSomethingElse = (value: string) => {
    if (value.trim()) setNoneOfTheAbove(false);
    setSomethingElse(value);
  };

  const toggleNoneOfTheAbove = () => {
    if (!noneOfTheAbove) {
      setSelectedIds([]);
      setSomethingElse("");
    }
    setNoneOfTheAbove(!noneOfTheAbove);
  };

  const typed = somethingElse.trim();
  const count = selectedIds.length + (typed ? 1 : 0);

  const confirm = () => {
    const chosen = medications.filter((m) =>
      selectedIds.includes(m.patient_vendor_medication_history_id),
    );
    // Free text isn't a Surescripts-verified medication, so it rides along in `labels` as part of
    // the message rather than being saved as an active medication. A renewal then requires a photo.
    onConfirm({
      medicationHistoryIds: selectedIds,
      labels: [...chosen.map(nameOf), ...(typed ? [typed] : [])],
    });
  };

  return (
    <>
      {medications.length === 0 && (
        <Paragraph
          size="$sm"
          color="$textSecondary"
          paddingHorizontal="$150"
          paddingBottom="$150"
        >
          {emptyDetail}
        </Paragraph>
      )}
      {medications.map((medication, index) => {
        const id = medication.patient_vendor_medication_history_id;
        const checked = selectedIds.includes(id);
        return (
          <YStack key={id}>
            {index > 0 && <OptionSeparator />}
            <OptionBodyHoverWrapper>
              <OptionBody
                label={nameOf(medication)}
                checked={checked}
                badge={<CheckboxBadge checked={checked} />}
                onPress={() => toggle(id)}
              />
            </OptionBodyHoverWrapper>
          </YStack>
        );
      })}
      <OptionSeparator />
      <SomethingElseRow
        value={somethingElse}
        onChangeText={changeSomethingElse}
      />
      <OptionSeparator />
      {/* With nothing pulled there is nothing to rule out, and a failed pull must not read as none. */}
      {medications.length > 0 && (
        <>
          <OptionBodyHoverWrapper>
            <OptionBody
              label="None of the above"
              checked={noneOfTheAbove}
              badge={<CheckboxBadge checked={noneOfTheAbove} />}
              onPress={toggleNoneOfTheAbove}
            />
          </OptionBodyHoverWrapper>
          <OptionSeparator />
        </>
      )}
      <XStack
        alignItems="center"
        gap="$200"
        paddingHorizontal="$150"
        paddingBottom="$150"
        marginTop="$250"
      >
        <Paragraph flex={1} size="$xs" color="$textSecondary">
          {count} selected
        </Paragraph>
        <SkipOrSubmit
          showSubmit
          submitDisabled={(count === 0 && !noneOfTheAbove) || saving}
          isLast
          onSkip={onSkip}
          onSubmit={confirm}
        />
      </XStack>
    </>
  );
}
